"use client";

import { useEffect, useState } from "react";
import { Icon } from "@to/ui";

import {
  AD_DEFAULT_MARKET_LAT,
  AD_DEFAULT_MARKET_LON,
} from "@/lib/theme-defaults";

/**
 * WEATHER CHIP (Task 37.3; shell chrome since the home v1 reskin) — a small
 * current-conditions pill, ported from the v1 `weather-badge.tsx` and
 * re-tokenized: v1's inline `<svg>` glyphs become Material Symbols `Icon`s and
 * v1's `bg-brand-teal/5` etc. become `--brand-*` tokens. OPTIONAL chrome — it
 * never blocks the page and never causes layout shift: it renders `null` until
 * the (client-only) fetch resolves, and it skips the fetch entirely below the
 * md breakpoint (the Nav hides it there — see the effect guard).
 *
 * Coordinates: AD has NO per-org coordinate source yet (no lat/lng on
 * `ad_org_config` / `synced_locations`), so when `lat`/`lng` aren't passed we
 * fall back to the launch market — Bonita Springs / SW Florida. This is the
 * documented fallback AND a forward seam: Phase-40 org admin can supply real
 * coordinates and pass them down as props with no change here.
 */

// Launch-market fallback coordinates live in theme-defaults.ts
// (AD_DEFAULT_MARKET_LAT/LON) so every AD platform default has one home; this
// chip falls back to them when no per-org lat/lng is passed.
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min — mirrors the v1 session-cache window.

/**
 * WMO `weathercode` → Material Symbols Rounded ligature. Every glyph here is
 * validated against the installed font by `shell-icons.test.ts`. Codes not
 * listed fall through to "sunny".
 */
const WMO_ICON: Record<number, string> = {
  0: "sunny",
  1: "sunny",
  2: "partly_cloudy_day",
  3: "cloud",
  45: "foggy",
  48: "foggy",
  51: "rainy",
  53: "rainy",
  55: "rainy",
  61: "rainy",
  63: "rainy",
  65: "rainy",
  80: "rainy",
  81: "rainy",
  82: "rainy",
  71: "weather_snowy",
  73: "weather_snowy",
  75: "weather_snowy",
  77: "weather_snowy",
  85: "weather_snowy",
  86: "weather_snowy",
  95: "thunderstorm",
  96: "thunderstorm",
  99: "thunderstorm",
};

/** Short human label per WMO code, for the (mobile-hidden) label span. */
const WMO_LABEL: Record<number, string> = {
  0: "Clear",
  1: "Mostly Clear",
  2: "Partly Cloudy",
  3: "Overcast",
  45: "Foggy",
  48: "Fog",
  51: "Light Drizzle",
  53: "Drizzle",
  55: "Heavy Drizzle",
  61: "Light Rain",
  63: "Rain",
  65: "Heavy Rain",
  80: "Showers",
  81: "Showers",
  82: "Heavy Showers",
  71: "Light Snow",
  73: "Snow",
  75: "Heavy Snow",
  77: "Snow Grains",
  85: "Snow Showers",
  86: "Snow Showers",
  95: "Thunderstorm",
  96: "Thunderstorm",
  99: "Severe Storm",
};

function iconForCode(code: number): string {
  return WMO_ICON[code] ?? "sunny";
}

function labelForCode(code: number): string {
  return WMO_LABEL[code] ?? "Fair";
}

interface WeatherData {
  temp: number;
  code: number;
}

interface WeatherChipProps {
  /** Latitude; falls back to the launch market when omitted. */
  lat?: number;
  /** Longitude; falls back to the launch market when omitted. */
  lng?: number;
}

export function WeatherChip({ lat, lng }: WeatherChipProps) {
  const [weather, setWeather] = useState<WeatherData | null>(null);

  useEffect(() => {
    // The shell shows the chip md+ only (`hidden md:flex` in Nav) — skip the
    // network round trip when the viewport can't display it. One-time check by
    // design: a mid-session resize across the breakpoint misses until remount
    // (chrome-level chip; not worth a resize listener). matchMedia is absent in
    // jsdom → treat as visible.
    const canShow =
      typeof window.matchMedia !== "function" ||
      window.matchMedia("(min-width: 768px)").matches;
    if (!canShow) return;

    const latitude = lat ?? AD_DEFAULT_MARKET_LAT;
    const longitude = lng ?? AD_DEFAULT_MARKET_LON;
    const cacheKey = `ad-weather-${latitude}-${longitude}`;
    let cancelled = false;
    // Apply a reading, guarded against a setState-after-unmount. Both paths go
    // through here off a microtask / promise callback — never a SYNCHRONOUS
    // setState in the effect body (react-hooks/set-state-in-effect → cascading
    // renders).
    const apply = (data: WeatherData) => {
      if (!cancelled) setWeather(data);
    };

    // Session cache (v1 pattern): a fresh entry (< 30 min) short-circuits the
    // network call — a second mount within the TTL re-uses it and does NOT fetch.
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached) as { data: WeatherData; ts: number };
        if (Date.now() - parsed.ts < CACHE_TTL_MS) {
          void Promise.resolve().then(() => apply(parsed.data));
          return () => {
            cancelled = true;
          };
        }
      }
    } catch {
      // Corrupt/absent cache — fall through to a live fetch.
    }

    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true&temperature_unit=fahrenheit&windspeed_unit=mph`,
    )
      .then((r) => r.json())
      .then((d) => {
        if (!d?.current_weather) return;
        const data: WeatherData = {
          temp: d.current_weather.temperature as number,
          code: d.current_weather.weathercode as number,
        };
        apply(data);
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({ data, ts: Date.now() }),
          );
        } catch {
          // Storage full / unavailable — non-fatal, the chip still renders.
        }
      })
      .catch(() => {
        // Network error — stay null (no chip), never throw.
      });

    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  // No layout shift, never blocks: render nothing until conditions load.
  if (!weather) return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium"
      style={{
        backgroundColor: "var(--brand-surface-subtle)",
        color: "var(--brand-text-muted)",
      }}
    >
      <Icon name={iconForCode(weather.code)} size={14} />
      <span>{Math.round(weather.temp)}&deg;F</span>
      <span className="hidden sm:inline">{labelForCode(weather.code)}</span>
    </span>
  );
}
